import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSnackbar } from 'notistack';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useCustomers } from '@/api/customers/use-customers';
import { useCreateTransaction } from '@/api/transactions/use-crm-transactions.tsx';
import { useCustomerTradingAccounts } from '@/api/trading-accounts/use-trading-accounts';
import { usePaymentMethodsList } from '@/api/payment-methods/use-payment-methods';

const transactionSchema = z
    .object({
        customer_id: z.string().min(1, 'Customer is required'),
        trading_account_id: z.string().min(1, 'Trading account is required'),
        type: z.enum(['deposit', 'withdrawal', 'bonus']),
        amount: z.number().positive('Amount must be positive'),
        currency: z.string().optional(),
        payment_method_id: z.string().optional(),
        payment_address: z.string().optional(),
        card_number: z.string().optional(),
        card_holder_name: z.string().optional(),
        card_expiry: z.string().optional(),
        card_cvv: z.string().optional(),
        reference_number: z.string().optional(),
        description: z.string().optional(),
        notes: z.string().optional(),
    })
    .refine(
        data => {
            // For deposit and withdrawal, payment method is required
            if (data.type === 'deposit' || data.type === 'withdrawal') {
                return data.payment_method_id && data.payment_method_id.length > 0;
            }
            return true;
        },
        {
            message: 'Payment method is required for deposits and withdrawals',
            path: ['payment_method_id'],
        }
    );

type TransactionFormData = z.infer<typeof transactionSchema>;

interface GeneralTransactionModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    customerId?: string;
}

const GeneralTransactionModal: React.FC<GeneralTransactionModalProps> = ({
    open,
    onClose,
    onSuccess,
    customerId,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const createTransaction = useCreateTransaction();
    const { data: customers } = useCustomers();
    const { data: tradingAccountsData } = useCustomerTradingAccounts(customerId || '');

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors },
    } = useForm<TransactionFormData>({
        resolver: zodResolver(transactionSchema),
        defaultValues: {
            customer_id: customerId || '',
            type: 'deposit',
            currency: 'USD',
        },
    });

    const selectedCustomerId = watch('customer_id');
    const selectedTradingAccountId = watch('trading_account_id');
    const selectedType = watch('type');
    const selectedPaymentMethodId = watch('payment_method_id');
    const selectedCurrency = watch('currency');

    // Get payment methods based on selected currency
    const {
        data: paymentMethodsData,
        error: paymentMethodsError,
        isLoading: paymentMethodsLoading,
    } = usePaymentMethodsList({
        is_active: true,
        currency: selectedCurrency || undefined,
    });

    // Get trading accounts for selected customer
    const selectedCustomer = customers?.find((c: any) => c.id === selectedCustomerId);
    const tradingAccounts = customerId
        ? tradingAccountsData?.accounts || []
        : (selectedCustomer as any)?.trading_accounts || [];

    // Get payment methods
    const paymentMethods = paymentMethodsData?.data || [];
    const selectedPaymentMethod = paymentMethods.find(
        (pm: any) => pm.id === selectedPaymentMethodId
    );

    // Check if payment method fields should be shown
    const showPaymentMethodFields = selectedType !== 'bonus';

    // Auto-populate currency when trading account changes
    React.useEffect(() => {
        if (selectedTradingAccountId && tradingAccounts.length > 0) {
            const selectedAccount = tradingAccounts.find(
                (account: any) => account.id === selectedTradingAccountId
            );
            if (selectedAccount?.currency) {
                setValue('currency', selectedAccount.currency);
            }
        }
    }, [selectedTradingAccountId, tradingAccounts, setValue]);

    // Clear payment method fields when switching to bonus
    React.useEffect(() => {
        if (selectedType === 'bonus') {
            setValue('payment_method_id', '');
            setValue('payment_address', '');
            setValue('card_number', '');
            setValue('card_holder_name', '');
            setValue('card_expiry', '');
            setValue('card_cvv', '');
        }
    }, [selectedType, setValue]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit = async (data: TransactionFormData) => {
        setIsSubmitting(true);
        try {
            await createTransaction.mutateAsync(data);
            enqueueSnackbar('Transaction created successfully', { variant: 'success' });
            reset();
            onSuccess();
            handleClose();
        } catch (error) {
            enqueueSnackbar('Failed to create transaction', { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Create New Transaction</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        {!customerId && (
                            <div className="space-y-2">
                                <Label htmlFor="customer_id">Customer *</Label>
                                <Select
                                    value={selectedCustomerId}
                                    onValueChange={value => setValue('customer_id', value)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select customer" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {customers?.map((customer: any) => (
                                            <SelectItem key={customer.id} value={customer.id}>
                                                {customer.name} ({customer.email})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.customer_id && (
                                    <p className="text-sm text-red-500">
                                        {errors.customer_id.message}
                                    </p>
                                )}
                            </div>
                        )}

                        <div className={`space-y-2 ${customerId ? 'col-span-2' : ''}`}>
                            <Label htmlFor="trading_account_id">Trading Account *</Label>
                            <Select
                                value={watch('trading_account_id')}
                                onValueChange={value => setValue('trading_account_id', value)}
                                disabled={!selectedCustomerId && !customerId}
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select account" />
                                </SelectTrigger>
                                <SelectContent className="w-full">
                                    {tradingAccounts.map((account: any) => (
                                        <SelectItem
                                            key={account.id}
                                            value={account.id}
                                            className="w-full"
                                        >
                                            <div className="flex justify-between items-center w-full">
                                                <div className="flex flex-col">
                                                    <span className="font-medium">
                                                        Account #
                                                        {account.account_number ||
                                                            account.id.slice(-8)}
                                                    </span>
                                                    <span className="text-sm text-gray-500">
                                                        {account.base_currency} •{' '}
                                                        {account.account_name || 'Trading Account'}
                                                    </span>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-medium">
                                                        {account.balance?.toLocaleString('en-US', {
                                                            style: 'currency',
                                                            currency:
                                                                account.base_currency || 'USD',
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        }) || 'N/A'}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        Leverage: {account.leverage}x
                                                    </div>
                                                </div>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {errors.trading_account_id && (
                                <p className="text-sm text-red-500">
                                    {errors.trading_account_id.message}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="type">Type *</Label>
                            <Select
                                value={selectedType}
                                onValueChange={value =>
                                    setValue('type', value as 'deposit' | 'withdrawal' | 'bonus')
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="deposit">Deposit</SelectItem>
                                    <SelectItem value="withdrawal">Withdrawal</SelectItem>
                                    <SelectItem value="bonus">Bonus</SelectItem>
                                </SelectContent>
                            </Select>
                            {errors.type && (
                                <p className="text-sm text-red-500">{errors.type.message}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="amount">Amount *</Label>
                            <Input
                                type="number"
                                step="0.01"
                                {...register('amount', { valueAsNumber: true })}
                                placeholder="0.00"
                            />
                            {errors.amount && (
                                <p className="text-sm text-red-500">{errors.amount.message}</p>
                            )}
                        </div>

                        {/* Currency is automatically set from selected trading account */}
                    </div>

                    {/* Payment Method Section - Only show for deposit/withdrawal */}
                    {showPaymentMethodFields && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="payment_method_id">Payment Method *</Label>
                                <Select
                                    value={selectedPaymentMethodId}
                                    onValueChange={value => setValue('payment_method_id', value)}
                                    disabled={paymentMethodsLoading}
                                >
                                    <SelectTrigger>
                                        <SelectValue
                                            placeholder={
                                                paymentMethodsLoading
                                                    ? 'Loading payment methods...'
                                                    : paymentMethodsError
                                                      ? 'Error loading payment methods'
                                                      : 'Select payment method'
                                            }
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {paymentMethodsLoading ? (
                                            <SelectItem value="loading" disabled>
                                                Loading payment methods...
                                            </SelectItem>
                                        ) : paymentMethodsError ? (
                                            <SelectItem value="error" disabled>
                                                Error loading payment methods
                                            </SelectItem>
                                        ) : paymentMethods.length === 0 ? (
                                            <SelectItem value="empty" disabled>
                                                No payment methods available
                                            </SelectItem>
                                        ) : (
                                            paymentMethods.map(paymentMethod => (
                                                <SelectItem
                                                    key={paymentMethod.id}
                                                    value={paymentMethod.id}
                                                >
                                                    {paymentMethod.display_name} (
                                                    {paymentMethod.code})
                                                </SelectItem>
                                            ))
                                        )}
                                    </SelectContent>
                                </Select>
                                {errors.payment_method_id && (
                                    <p className="text-sm text-red-500">
                                        {errors.payment_method_id.message}
                                    </p>
                                )}
                                {paymentMethodsError && (
                                    <p className="text-sm text-red-500">
                                        Failed to load payment methods:{' '}
                                        {paymentMethodsError.message}
                                    </p>
                                )}
                            </div>

                            {/* Conditional fields based on selected payment method */}
                            {selectedPaymentMethod && (
                                <div className="space-y-4">
                                    {selectedPaymentMethod.requires_address && (
                                        <div className="space-y-2">
                                            <Label htmlFor="payment_address">
                                                Payment Address *
                                            </Label>
                                            <Input
                                                {...register('payment_address')}
                                                placeholder="Enter payment address"
                                            />
                                            {errors.payment_address && (
                                                <p className="text-sm text-red-500">
                                                    {errors.payment_address.message}
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {selectedPaymentMethod.requires_card_details && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="card_number">Card Number *</Label>
                                                <Input
                                                    {...register('card_number')}
                                                    placeholder="1234 5678 9012 3456"
                                                />
                                                {errors.card_number && (
                                                    <p className="text-sm text-red-500">
                                                        {errors.card_number.message}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="card_holder_name">
                                                    Card Holder Name *
                                                </Label>
                                                <Input
                                                    {...register('card_holder_name')}
                                                    placeholder="John Doe"
                                                />
                                                {errors.card_holder_name && (
                                                    <p className="text-sm text-red-500">
                                                        {errors.card_holder_name.message}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="card_expiry">Expiry Date *</Label>
                                                <Input
                                                    {...register('card_expiry')}
                                                    placeholder="MM/YY"
                                                />
                                                {errors.card_expiry && (
                                                    <p className="text-sm text-red-500">
                                                        {errors.card_expiry.message}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="card_cvv">CVV *</Label>
                                                <Input
                                                    {...register('card_cvv')}
                                                    placeholder="123"
                                                    type="password"
                                                />
                                                {errors.card_cvv && (
                                                    <p className="text-sm text-red-500">
                                                        {errors.card_cvv.message}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="reference_number">Reference Number</Label>
                        <Input
                            {...register('reference_number')}
                            placeholder="Transaction reference number"
                        />
                        {errors.reference_number && (
                            <p className="text-sm text-red-500">
                                {errors.reference_number.message}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Input
                            {...register('description')}
                            placeholder="Brief description of the transaction"
                        />
                        {errors.description && (
                            <p className="text-sm text-red-500">{errors.description.message}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="notes">Notes</Label>
                        <Textarea
                            {...register('notes')}
                            placeholder="Additional notes (optional)"
                            rows={3}
                        />
                        {errors.notes && (
                            <p className="text-sm text-red-500">{errors.notes.message}</p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Creating...' : 'Create Transaction'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default GeneralTransactionModal;
